import React, { useRef, useEffect, useState } from "react";
import {
  Button,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grid,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { QrCodeScanner, Send, Clear } from "@mui/icons-material";
import AppForm from "components/Shared/AppForm";

const ScanResultBanner = ({ result }) => {
  if (!result) return null;
  const config = {
    tallied: {
      bg: "#ecfdf5",
      color: "#047857",
      border: "#a7f3d0",
      icon: "✅",
      label: "Tallied",
    },
    missingSystem: {
      bg: "#faf5ff",
      color: "#7e22ce",
      border: "#e9d5ff",
      icon: "❓",
      label: "Not in this branch",
    },
    duplicate: {
      bg: "#fef2f2",
      color: "#b91c1c",
      border: "#fecaca",
      icon: "⚠️",
      label: "Already scanned",
    },
    error: {
      bg: "#fff7ed",
      color: "#c2410c",
      border: "#fed7aa",
      icon: "❌",
      label: "Error",
    },
  };
  const cfg = config[result.type] || config.error;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        mt: 2,
        p: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: theme.palette.mode === "dark" ? `${cfg.color}1A` : cfg.bg, 
        border: `1px solid ${
          theme.palette.mode === "dark" ? `${cfg.color}40` : cfg.border
        }`,
        borderRadius: 2,
      })}
    >
      <Typography fontSize={22}>{cfg.icon}</Typography>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: cfg.color, lineHeight: 1.2 }}
        >
          {cfg.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: cfg.color, opacity: 0.8, display: "block" }}
        >
          {result.message}
        </Typography>
      </Box>
      <Box
        sx={{
          fontFamily: "monospace",
          fontWeight: 800,
          fontSize: 13,
          color: cfg.color,
          bgcolor: "rgba(0,0,0,0.05)",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
        }}
      >
        {result.shipmentId}
      </Box>
    </Paper>
  );
};

const ScanLogRow = ({ scan, index }) => {
  const typeConfig = {
    tallied: { icon: "✅", color: "#059669", bg: "#f0fdf4" },
    missingSystem: { icon: "❓", color: "#9333ea", bg: "#fdf4ff" },
    duplicate: { icon: "⚠️", color: "#dc2626", bg: "#fff5f5" },
  };
  const cfg = typeConfig[scan.type] || typeConfig.missingSystem;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 16px",
        background: index === 0 ? cfg.bg : "#fff",
        borderBottom: "1px solid #f1f5f9",
        transition: "background 0.3s",
      }}
    >
      <span style={{ fontSize: 16, flexShrink: 0 }}>{cfg.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            fontFamily: "monospace",
            fontWeight: 700,
            fontSize: 13,
            color: cfg.color,
          }}
        >
          {scan.shipmentId}
        </span>
        {scan.scannedByName && scan.scannedByName !== "You" && (
          <span style={{ marginLeft: 8, fontSize: 11, color: "#94a3b8" }}>
            by {scan.scannedByName}
          </span>
        )}
      </div>
      <span
        style={{
          fontSize: 11,
          color: "#94a3b8",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {new Date(scan.time).toLocaleTimeString("en-NP", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </span>
    </div>
  );
};

const BranchTallyScanForm = ({
  isBusy = false,
  onScan = () => {},
  scanLog = [],
  lastResult = null,
  tallyInfo = null,
  stats = {},
  isOnHold = false,
}) => {
  const inputRef = useRef(null);
  const [shipmentId, setShipmentId] = useState("");

  useEffect(() => {
    if (!isOnHold) inputRef.current?.focus();
  }, [lastResult, isOnHold]);

  const handleSubmit = () => {
    const id = shipmentId.trim();
    if (!id || isBusy || isOnHold) return;
    onScan({ shipmentId: id });
    setShipmentId("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <AppForm>
      <AppForm.Content>
        {tallyInfo && (
          <Paper
            elevation={0}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, md: 4 },
              p: 2,
              mb: 3,
              bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8fafc", // Gray in dark mode
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#374151" : "#e2e8f0"
              }`,
              borderRadius: 2,
              flexWrap: "wrap",
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 150,
              }}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  fontWeight: 700,
                  color: theme.palette.mode === "dark" ? "grey.400" : "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                })}
              >
                Active Session
              </Typography>
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontWeight: 700,
                  color:
                    theme.palette.mode === "dark" ? "common.white" : "#0f172a",
                })}
              >
                {tallyInfo?.branch?.name || "Unknown Branch"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: { xs: 2, md: 4 } }}>
              {[
                {
                  label: "Scanned",
                  value: stats.totalScanned ?? 0,
                  color: "#d97706",
                },
                {
                  label: "Tallied",
                  value: stats.talliedCount ?? 0,
                  color: "#059669",
                },
                {
                  label: "Not in branch",
                  value: stats.missingSystemCount ?? 0,
                  color: "#9333ea",
                },
              ].map((s, index) => (
                <Box
                  key={s.label}
                  sx={(theme) => ({
                    textAlign: "center",
                    pl: index !== 0 ? { xs: 2, md: 4 } : 0,
                    borderLeft:
                      index !== 0
                        ? `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#374151"
                              : "#e2e8f0"
                          }`
                        : "none",
                  })}
                >
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 900,
                      color: s.color,
                      fontFamily: "monospace",
                      lineHeight: 1.1,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: s.color,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Input Area */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="medium"
              label="Scan / Enter Shipment ID"
              placeholder={
                isOnHold ? "Scanning unavailable…" : "e.g. 3D8P-4X4C-OR23"
              }
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              disabled={isOnHold}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeScanner
                      fontSize="small"
                      sx={{ color: isOnHold ? "#94a3b8" : "#2563eb" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: shipmentId ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShipmentId("")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                style: {
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: 15,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: isOnHold ? "#e2e8f0" : "#2563eb",
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: isOnHold ? "#e2e8f0" : "#1d4ed8",
                  },
                  "&.Mui-focused fieldset": { borderColor: "#1d4ed8" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!shipmentId.trim() || isBusy || isOnHold}
              onClick={handleSubmit}
              startIcon={
                isBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send fontSize="small" />
                )
              }
              sx={{ height: 52, fontWeight: 700, boxShadow: "none" }}
            >
              {isBusy ? "Scanning…" : "Scan"}
            </Button>
          </Grid>
        </Grid>

        <ScanResultBanner result={lastResult} />

        {/* Scan Log Area */}
        {scanLog.length > 0 && (
          <Paper
            elevation={0}
            sx={(theme) => ({
              mt: 3,
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#374151" : "#e2e8f0"
              }`,
              borderRadius: 2,
              overflow: "hidden",
            })}
          >
            <Box
              sx={(theme) => ({
                p: 1.5,
                px: 2,
                bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f8fafc", // Gray in dark mode
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#374151" : "#e2e8f0"
                }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              })}
            >
              <Typography
                variant="subtitle2"
                sx={(theme) => ({
                  fontWeight: 700,
                  color: theme.palette.mode === "dark" ? "grey.300" : "#475569",
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                })}
              >
                Scan Log
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: theme.palette.mode === "dark" ? "grey.400" : "#94a3b8",
                })}
              >
                {scanLog.length} {scanLog.length === 1 ? "Scan" : "Scans"} •
                Most recent first
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
              {scanLog.map((scan, i) => (
                <ScanLogRow key={scan._id || i} scan={scan} index={i} />
              ))}
            </Box>
          </Paper>
        )}
      </AppForm.Content>
    </AppForm>
  );
};

export default BranchTallyScanForm;
